import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { PasswordInput } from 'src/design/atoms/PasswordInput';
import { Stack } from 'src/design/atoms/Stack';
import { Switch } from 'src/design/atoms/Switch';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { ApiConnectionDto } from 'src/api/FiretrolApiClient';
import {
  useCreateApiConnection,
  useDeleteApiConnection,
  useUpdateApiConnection,
} from 'src/api/queries/admin/useApiConnectionMutations';
import { getErrorMessage } from 'src/utils';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { ModalButton } from 'src/design/molecules/ModalButton';

const baseUrlValidator = Yup.string()
  .required('Base URL is required')
  .max(500)
  .test('is-http-url', 'Must be a valid HTTP or HTTPS URL', (value) => {
    if (!value) return false;
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  });

const nameValidator = Yup.string().required('Name is required').max(255);

const createValidationSchema = Yup.object().shape({
  name: nameValidator,
  baseUrl: baseUrlValidator,
  apiKey: Yup.string().required('API key is required'),
});

const updateValidationSchema = Yup.object().shape({
  name: nameValidator,
  baseUrl: baseUrlValidator,
  apiKey: Yup.string().optional(),
});

interface ApiConnectionFormProps {
  connection: ApiConnectionDto | null;
  onClose: () => void;
  onSave: () => void;
}

export const ApiConnectionForm: React.FC<ApiConnectionFormProps> = ({
  connection,
  onClose,
  onSave,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const createConnection = useCreateApiConnection();
  const updateConnection = useUpdateApiConnection();
  const deleteConnection = useDeleteApiConnection();

  const formik = useFormik({
    initialValues: {
      name: connection?.name ?? '',
      baseUrl: connection?.baseUrl ?? '',
      apiKey: '',
      isEnabled: connection?.isEnabled ?? true,
    },
    validationSchema: connection
      ? updateValidationSchema
      : createValidationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setError(null);
      try {
        if (connection) {
          await updateConnection.mutateAsync({
            id: connection.id,
            request: {
              name: values.name,
              baseUrl: values.baseUrl,
              apiKey: values.apiKey || undefined,
              isEnabled: values.isEnabled,
            },
          });
        } else {
          await createConnection.mutateAsync({
            name: values.name,
            baseUrl: values.baseUrl,
            apiKey: values.apiKey,
            isEnabled: values.isEnabled,
          });
        }
        onSave();
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      }
    },
  });

  const handleDelete = () => {
    if (!connection) return;
    deleteConnection.mutate(connection.id, { onSuccess: () => onSave() });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  return (
    <>
      <Box>
        <Stack>
          <TextInput
            label="Name"
            placeholder="Connection name..."
            required
            value={formik.values.name}
            onChange={(e) => formik.setFieldValue('name', e.target.value)}
            onBlur={() => formik.setFieldTouched('name')}
            error={formik.touched.name && formik.errors.name}
            onKeyDown={handleKeyDown}
          />

          <TextInput
            label="Base URL"
            description="Host root only. Each integration adds its own path."
            placeholder="https://app.example.com"
            required
            value={formik.values.baseUrl}
            onChange={(e) => formik.setFieldValue('baseUrl', e.target.value)}
            onBlur={() => formik.setFieldTouched('baseUrl')}
            error={formik.touched.baseUrl && formik.errors.baseUrl}
            onKeyDown={handleKeyDown}
          />

          <PasswordInput
            label="API Key"
            placeholder={
              connection
                ? 'Leave blank to keep existing key'
                : 'Enter API key...'
            }
            required={!connection}
            value={formik.values.apiKey}
            onChange={(e) => formik.setFieldValue('apiKey', e.target.value)}
            onBlur={() => formik.setFieldTouched('apiKey')}
            error={formik.touched.apiKey && formik.errors.apiKey}
            description={
              connection
                ? `Current key: ${connection.redactedApiKey || 'none'}`
                : undefined
            }
          />

          <Switch
            label="Enabled"
            description="A disabled connection blocks every integration that uses it."
            checked={formik.values.isEnabled}
            onChange={(e) =>
              formik.setFieldValue('isEnabled', e.currentTarget.checked)
            }
          />

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}
        </Stack>
      </Box>

      <Group justify="space-between" mt="md">
        {connection && (
          <InlineConfirmButton
            color="red"
            confirmButtonProps={{ children: 'Confirm Delete' }}
            onConfirmClick={handleDelete}
          >
            Delete Connection
          </InlineConfirmButton>
        )}
        <Group>
          <ModalButton onClick={onClose} color="gray" variant="light">
            Cancel
          </ModalButton>
          <ModalButton
            onClick={() => formik.handleSubmit()}
            loading={formik.isSubmitting}
            disabled={!formik.isValid || (!!connection && !formik.dirty)}
          >
            {connection ? 'Save Changes' : 'Create Connection'}
          </ModalButton>
        </Group>
      </Group>
    </>
  );
};
